import CustomFormLabel from "Shared/Components/Inputs/CustomFormLabel";
import CustomTextField from "Shared/Components/Inputs/CustomTextField";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import type {
	SxProps,
	TextFieldProps,
	TextFieldVariants,
	Theme,
} from "@mui/material";
import { IconButton, Stack, Tooltip } from "@mui/material";
import {
	type Control,
	Controller,
	type FieldError,
	type FieldErrors,
	type FieldPath,
	type FieldValues,
	get,
} from "react-hook-form";
import { FormInputContainer } from "../Container/FormInputContainer";
import { useFieldError } from "../Helpers/getFormErrors";

type InputProps<FormType extends FieldValues> = TextFieldProps & {
	variant: TextFieldVariants;
	id: string;
	label?: string;
	fullWidth?: boolean;
	type?: "text" | "number" | "range" | "date" | "password";
	InputLabelProps?: Record<string, boolean>;
	name: FieldPath<FormType>;
	control: Control<FormType, object>;
	errors: FieldErrors<FormType>;
	maxLength?: number;
	disabled?: boolean;
	isNumber?: boolean;
	isFloat?: boolean;
	maxRows?: number;
	rows?: number;
	multiline?: boolean;
	readOnly?: boolean;
	slotProps?: TextFieldProps["slotProps"];
	sxLabel?: SxProps<Theme>;
	labelTooltip?: string;
	startAdornment?: JSX.Element;
	endAdornment?: JSX.Element;
	inputChangeRegex?: RegExp;
	onChange?: (
		event:
			| React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>
			| unknown,
	) => void;
};

const Input = <FormType extends FieldValues>({
	id,
	label,
	variant = "outlined",
	fullWidth = false,
	type,
	name,
	control,
	errors,
	maxLength,
	disabled,
	isNumber,
	isFloat,
	maxRows,
	rows,
	multiline,
	readOnly,
	sx,
	startAdornment,
	endAdornment,
	sxLabel,
	labelTooltip,
	inputChangeRegex,
	...rest
}: InputProps<FormType>) => {
	const { hasError, errorMessage } = useFieldError({
		fieldError: get(errors, name) as FieldError,
	});

	return (
		<>
			{label && (
				<CustomFormLabel sx={sxLabel} htmlFor={id}>
					<Stack direction="row" alignItems="center" gap={0.5} component="span">
						{label}
						{labelTooltip && (
							<Tooltip title={labelTooltip} arrow>
								<IconButton size="small" sx={{ padding: 0, fontSize: 16 }}>
									<InfoOutlinedIcon
										sx={{ fontSize: 16, color: "text.secondary" }}
									/>
								</IconButton>
							</Tooltip>
						)}
					</Stack>
				</CustomFormLabel>
			)}
			<Controller
				name={name}
				control={control}
				render={({ field: { onChange, value } }) => (
					<FormInputContainer>
						<CustomTextField
							id={id}
							variant={variant}
							fullWidth={fullWidth}
							value={value}
							name={name.toString()}
							type={type ?? "text"}
							disabled={disabled}
							inputProps={{ maxLength, readOnly }}
							error={!!hasError}
							helperText={!!hasError ? errorMessage : ""}
							multiline={multiline}
							rows={rows}
							maxRows={maxRows}
							sx={sx}
							slotProps={{
								input: {
									sx: {
										...(startAdornment && { paddingLeft: 0 }),
										...(endAdornment && { paddingRight: 0 }),
									},
									...(startAdornment && { startAdornment }),
									...(endAdornment && { endAdornment }),
								},
							}}
							onBlur={(e) => {
								let val: string | number = e.target.value;
								if (isNumber && isFloat) {
									val = Number.isNaN(Number.parseFloat(val))
										? 0
										: Number.parseFloat(val);
									onChange(val);
								}
							}}
							onChange={(
								e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
							) => {
								let val: string | number = e.target.value;

								if (inputChangeRegex !== undefined) {
									val = val.replace(inputChangeRegex, "");
								}

								if (isNumber && isFloat) {
									val = val.replace(/[^0-9.-]/g, "").replace(/\.(?=.*\.)/g, "");

									if (Number.isNaN(val)) {
										val = 0;
									}
								} else if (isNumber && !isFloat && val === "-") {
								} else if (isNumber && !isFloat) {
									val = Number(val.replace(/[^0-9-]/g, ""));
								}
								onChange(val);
								rest.onChange?.(e.currentTarget.value);
							}}
							{...rest}
						/>
					</FormInputContainer>
				)}
			/>
		</>
	);
};

export default Input;
